import React, { useCallback, useState, useEffect, useRef } from 'react';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import ScrollToTop from 'components/common/ScrollToTop';
import AccessRightSelector from 'components/common/AccessRightSelector';
import CircularLoader from 'components/common/CircularLoader';
import PatientInfo from 'components/common/PatientInfo';
import { PRESCRIPTION_STATUS } from 'utils/prescription';
import getHyliaApiClient from 'clients/hylia-api';
import {
  PatientInfoDTO,
  PatientOverviewDto,
  RewardListDto,
  PatientUsageReportDto,
  TreatmentProgressSummaryDto,
  AccessRightDTO,
  CurriculumProgressDTO,
  DailySummaryDto,
} from '@peartherapeutics/hylia-api-client';
import MainBody from './MainBody';
import LastClinicVisit from './LastClinicVisit';
import LessonSummaryChart from './LessonSummaryChart';
import styles from './styles.module.scss';
import { IProps } from './types';
import { DashboardContext, IDashboardContextType } from './context';

/**
 * Displays patient information.
 * @param props The props
 * @return A JSX Element
 */
function PatientDashboard({ patientId }: IProps): JSX.Element {
  const [selectedArId, setSelectedArId] = useState<string>();
  const [selectedAccessRight, setSelectedAccessRight] =
    useState<AccessRightDTO>();
  const [overview, setOverview] = useState<PatientOverviewDto>();
  const [rewardList, setRewardList] = useState<RewardListDto>();
  const [usageReport, setUsageReport] = useState<PatientUsageReportDto>();
  const [patientInfo, setPatientInfo] = useState<PatientInfoDTO>();
  const [dailySummaries, setDailySummaries] = useState<DailySummaryDto[]>();
  const [treatmentSummary, setTreatmentSummary] =
    useState<TreatmentProgressSummaryDto>();
  const [curriculumProgress, setCurriculumProgress] =
    useState<CurriculumProgressDTO>();

  const isMounted = useRef(false);
  const providerValue: IDashboardContextType = {
    selectedArId,
    setSelectedArId,
    selectedAccessRight,
    setSelectedAccessRight,
    patientId,
    overview,
    setOverview,
    rewardList,
    setRewardList,
    usageReport,
    setUsageReport,
    curriculumProgress,
    setCurriculumProgress,
    dailySummaries,
    setDailySummaries,
    treatmentSummary,
    setTreatmentSummary,
    patientInfo,
    setPatientInfo,
  };

  /**
   * Sets treatment summary (reported appointment compliance)
   * and clears usageReport so it can be refetched for the calendar
   * @param treatmentSummary
   */
  const setTreatmentSummaryAndInvalidateUsageReport = useCallback(
    (treatmentSummary) => {
      setTreatmentSummary({ ...treatmentSummary });
      setUsageReport(undefined);
    },
    [],
  );

  /**
   * Gets patient info from hylia-api.
   * @param patientId The patient id
   */
  const getPatientInfo = useCallback(async (patientId: string) => {
    try {
      const apiClient = await getHyliaApiClient();
      const { data: patientInfo } = await apiClient.getPatientInfo(patientId);
      const { accessRights } = patientInfo;
      if (isMounted.current) {
        setPatientInfo({ ...patientInfo });
        if (accessRights.length > 0) {
          setSelectedArId(accessRights[0]!.id);
          setSelectedAccessRight(accessRights[0]!);
        }
      }
    } catch (error) {
      ErrorInterceptor.error(error);
    }
  }, []);

  /**
   * Gets the treatment summary for the selected access right from hylia-api.
   */
  const getTreatmentSummary = useCallback(async () => {
    if (patientId && selectedArId) {
      try {
        const apiClient = await getHyliaApiClient();
        const { data: treatmentSummary } =
          await apiClient.getTreatmentProgressSummary(patientId, selectedArId);
        if (isMounted.current) {
          setTreatmentSummary({ ...treatmentSummary });
        }
      } catch (error) {
        ErrorInterceptor.error(error);
      }
    }
  }, [patientId, selectedArId]);

  // Gets the patient info
  useEffect(() => {
    if (patientId) {
      getPatientInfo(patientId);
    }
  }, [patientId, getPatientInfo]);

  // Gets the treatment summary when the selected ar changes
  useEffect(() => {
    getTreatmentSummary();
  }, [selectedArId, getTreatmentSummary]);

  // Keeps track of whether or not the component is mounted
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  });

  const selectedAr = patientInfo?.accessRights.find(
    (ar) => ar.id === selectedArId,
  );
  const missingAr = patientInfo?.accessRights.length === 0;
  const loading = !(patientInfo && treatmentSummary && selectedAr);
  const showLessonSummary = /started|expired/.test(selectedAr?.status || '');
  const showLastClinicVisit =
    selectedAr?.status === PRESCRIPTION_STATUS.STARTED;
  return (
    <div data-testid="patient-dashboard-iframe">
      {missingAr && (
        <div className={styles['no-ar-found']}>
          {`No access rights found for ${patientInfo.emailAddress}`}
        </div>
      )}
      {!missingAr && loading && <CircularLoader />}
      {!missingAr && !loading && (
        <DashboardContext.Provider value={providerValue}>
          <ScrollToTop immediate>
            <div
              className={styles.wrapper}
              data-testid="patient-dashboard-loaded"
            >
              <AccessRightSelector
                accessRights={patientInfo.accessRights}
                selectedArId={selectedArId}
                setSelectedArId={setSelectedArId}
                setSelectedAccessRight={setSelectedAccessRight}
                setOverview={setOverview}
                setRewardList={setRewardList}
                setUsageReport={setUsageReport}
                setCurriculumProgress={setCurriculumProgress}
              />
              <div
                className={styles['grid-container']}
                aria-label="Product and Patient Information"
              >
                <div className={styles['top-left']}>
                  <PatientInfo
                    firstName={patientInfo.firstName}
                    lastName={patientInfo.lastName}
                    dateOfBirth={patientInfo.dateOfBirth}
                    pearConnectRef={patientInfo.pearConnectRef}
                    duration={selectedAr.durationInDays}
                    startDate={selectedAr.startDate}
                    remainingDays={selectedAr.daysRemaining}
                    isExpired={selectedAr.status === 'expired'}
                    isSample={selectedAr.isSample}
                    program={selectedAr.program}
                    timezone={selectedAr.timezone}
                  />
                </div>
                <div
                  className={styles['top-middle']}
                  data-testid="lesson-summary"
                >
                  {showLessonSummary && (
                    <LessonSummaryChart
                      showHeader
                      lessonSummary={treatmentSummary.lessonSummary}
                      completedModuleAmount={
                        treatmentSummary.lessonSummary.completed
                      }
                      revisitedModuleAmount={
                        treatmentSummary.lessonSummary.revisitedModuleAmount
                      }
                      shouldHighLightWeekNumber={
                        selectedAr.status !== 'expired'
                      }
                    />
                  )}
                </div>
                <div className={styles['top-right']}>
                  {showLastClinicVisit ? (
                    <LastClinicVisit
                      treatmentSummary={treatmentSummary}
                      setTreatmentSummary={
                        setTreatmentSummaryAndInvalidateUsageReport
                      }
                      setOverview={setOverview}
                    />
                  ) : (
                    <div className={styles['no-border']} />
                  )}
                </div>
              </div>
              <MainBody accessRight={selectedAr} />
            </div>
          </ScrollToTop>
        </DashboardContext.Provider>
      )}
    </div>
  );
}

export default PatientDashboard;
